import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { agenciesAPI } from '../../services/api';
import Card from '../common/Card';
import Loading from '../common/Loading';
import Button from '../common/Button';
import StatusBadge from '../common/StatusBadge';
import { formatDate, handleApiError } from '../../utils/helpers';

const AgencyDetailPage = () => {
  const { id } = useParams();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAgency();
  }, [id]);

  const fetchAgency = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await agenciesAPI.getById(id);
      setAgency(response.data.agency);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error || !agency) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error || 'Agency not found'}</p>
        <Link to="/super-admin/agencies">
          <Button variant="secondary">Back to Agencies</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link to="/super-admin/agencies" className="text-primary-600 hover:text-primary-700 mb-2 inline-block">
            ← Back to Agencies
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">{agency.name}</h1>
          <p className="text-gray-600 mt-1">Agency details and information</p>
        </div>
        <Link to={`/super-admin/agencies/${id}/edit`}>
          <Button variant="primary">Edit Agency</Button>
        </Link>
      </div>

      {/* Agency Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Agency Name</dt>
              <dd className="text-sm text-gray-900 mt-1">{agency.name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1">
                <span
                  className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                    agency.is_active === false
                      ? 'bg-red-100 text-red-800'
                      : 'bg-green-100 text-green-800'
                  }`}
                >
                  {agency.is_active === false ? 'Inactive' : 'Active'}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Created At</dt>
              <dd className="text-sm text-gray-900 mt-1">{formatDate(agency.created_at)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
              <dd className="text-sm text-gray-900 mt-1">{formatDate(agency.updated_at)}</dd>
            </div>
          </dl>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Admin Information</h2>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Admin Name</dt>
              <dd className="text-sm text-gray-900 mt-1">{agency.admin_name || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Admin Email</dt>
              <dd className="text-sm text-gray-900 mt-1">{agency.admin_email || 'N/A'}</dd>
            </div>
          </dl>
        </Card>

        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription Information</h2>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-gray-500">Plan</dt>
              <dd className="text-sm text-gray-900 mt-1">{agency.plan_name || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Plan Price</dt>
              <dd className="text-sm text-gray-900 mt-1">
                ${agency.plan_price === 0 || !agency.plan_price ? 'Free' : agency.plan_price}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Subscription Status</dt>
              <dd className="mt-1">
                <StatusBadge status={agency.subscription_status} />
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Subscription Start</dt>
              <dd className="text-sm text-gray-900 mt-1">
                {formatDate(agency.subscription_start_date) || 'N/A'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Subscription End</dt>
              <dd className="text-sm text-gray-900 mt-1">
                {formatDate(agency.subscription_end_date) || 'N/A'}
              </dd>
            </div>
          </dl>
        </Card>
      </div>
    </div>
  );
};

export default AgencyDetailPage;








